/**
 * BlockName注册中心
 * 用于智能动画中确保同一作用域内的blockName唯一性
 *
 * 隔离规则：
 * - 每个面板(panelId)独立隔离
 * - 自定义视图的每条记录(recordId)独立隔离
 * - 同步组件(syncId)独立隔离
 * - 同一作用域下，每个blockName只有第一个可以注册layout动画
 */
class AnimationLayoutIdRegistry {
    // compositeKey: `${panelId}:${scopeKey}:${blockName}` -> { layoutId, blockId }
    private readonly registry = new Map<string, { layoutId: string; blockId: string }>()

    /**
     * 注册一个blockName，返回唯一的layoutId
     * @param panelId 面板ID
     * @param blockId block实例ID（用于区分同一block的更新 vs 不同block的重复）
     * @param blockName block的名称
     * @param scopeKey 作用域key（recordId/syncId等，用于隔离不同实例）
     * @returns 唯一的layoutId，如果该作用域下已有相同blockName且是不同blockId则返回undefined
     */
    register(panelId: string, blockId: string, blockName: string, scopeKey = 'default'): string | undefined {
        const key = `${panelId}:${scopeKey}:${blockName}`
        const existing = this.registry.get(key)

        // 如果该作用域下已有此blockName
        if (existing) {
            // 同一个blockId，返回已有的layoutId（允许组件更新时重新注册）
            if (existing.blockId === blockId) {
                return existing.layoutId
            }
            // 不同blockId，拒绝注册（同一作用域下blockName唯一）
            return
        }

        // 首次注册，使用blockName作为layoutId
        const layoutId = blockName
        this.registry.set(key, { layoutId, blockId })
        return layoutId
    }

    /**
     * 面板卸载时清理该面板下的所有注册
     */
    reset(panelId: string) {
        const prefix = `${panelId}:`
        for (const key of this.registry.keys()) {
            if (key.startsWith(prefix)) {
                this.registry.delete(key)
            }
        }
    }

    /**
     * 清空所有注册表
     */
    clear() {
        this.registry.clear()
    }

    /**
     * 获取面板下的所有注册（用于调试）
     */
    getRegisterContainer(panelId: string) {
        const prefix = `${panelId}:`
        const result = new Map<string, { layoutId: string; blockId: string }>()
        for (const [key, value] of this.registry.entries()) {
            if (key.startsWith(prefix)) {
                result.set(key, value)
            }
        }
        return result
    }
}

// 全局单例
export const animationLayoutIdRegistry = new AnimationLayoutIdRegistry()
